/**
 * As contas e as palavras da tela de inspeções. Fora do componente, porque é o
 * que dá para provar sem montar React.
 */
import { formatarAteDuasCasas, formatarInteiro, interpretarNumero } from '@/lib/formato';

import type {
  Criticidade,
  Desfecho,
  Inspecao,
  ItemDaInspecao,
  ItemDoChecklist,
  LinhaDeCobertura,
  Ocorrencia,
  RotinaDoChecklist,
  SituacaoDaInspecao,
  SituacaoDoChecklist,
} from './api';

export function rotinaEscrita(rotina: RotinaDoChecklist): string {
  if (rotina === 'diaria') return 'Diária';
  if (rotina === 'semanal') return 'Semanal';
  if (rotina === 'entrega') return 'Entrega de máquina';
  if (rotina === 'devolucao') return 'Devolução de máquina';
  return 'Depois da manutenção';
}

export function situacaoDoChecklistEscrita(situacao: SituacaoDoChecklist): string {
  if (situacao === 'rascunho') return 'Rascunho';
  if (situacao === 'publicado') return 'Publicado';
  return 'Arquivado';
}

export function situacaoDaInspecaoEscrita(situacao: SituacaoDaInspecao): string {
  if (situacao === 'em_andamento') return 'Em andamento';
  if (situacao === 'concluida') return 'Concluída';
  return 'Cancelada';
}

/**
 * A criticidade escrita.
 *
 * `informativo` existe no item e não na ocorrência — o item informativo não
 * abre achado. A função cobre os três porque é o item que ela descreve.
 */
export function criticidadeEscrita(criticidade: Criticidade): string {
  if (criticidade === 'informativo') return 'Informativo';
  if (criticidade === 'atencao') return 'Atenção';
  return 'Crítico';
}

export function desfechoEscrito(desfecho: Desfecho): string {
  if (desfecho === 'ordem_de_servico') return 'Virou ordem de serviço';
  if (desfecho === 'resolvida_no_local') return 'Resolvida no local';
  return 'Não procede';
}

export interface TotaisDaInspecao {
  /** Ocorrências em aberto, somando atenção e crítico. */
  readonly abertas: number;
  /** Só as críticas em aberto. É o número que não pode ser ignorado. */
  readonly criticasAbertas: number;
  /** Ocorrências que pedem bloqueio e ainda estão em aberto. */
  readonly pedemBloqueio: number;
  /** Máquinas que nunca foram inspecionadas por algum checklist publicado. */
  readonly nuncaInspecionadas: number;
  /** A maior quantidade de dias que uma ocorrência está em aberto. */
  readonly maisAntigaEmDias: number;
}

/**
 * Os totais da faixa.
 *
 * `nuncaInspecionadas` conta máquina com `ultimaEm` nulo, e conta MÁQUINA e
 * não linha: a mesma máquina pode ter dois checklists publicados, e contá-la
 * duas vezes inflaria o número que a tela mostra como alerta.
 */
export function totaisDaInspecao(
  ocorrencias: readonly Ocorrencia[],
  cobertura: readonly LinhaDeCobertura[],
): TotaisDaInspecao {
  const semInspecao = new Set(
    cobertura.filter((l) => l.ultimaEm === null).map((l) => l.equipamentoId),
  );
  return {
    abertas: ocorrencias.length,
    criticasAbertas: ocorrencias.filter((o) => o.criticidade === 'critico').length,
    pedemBloqueio: ocorrencias.filter((o) => o.pedeBloqueio).length,
    nuncaInspecionadas: semInspecao.size,
    maisAntigaEmDias: ocorrencias.reduce((maior, o) => Math.max(maior, o.diasAberta), 0),
  };
}

export type EstadoDaFaixa = 'carregando' | 'falhou' | 'pronta';

export function estadoDaFaixa(consulta: { readonly data: unknown; readonly isError: boolean }): EstadoDaFaixa {
  if (consulta.isError) return 'falhou';
  return consulta.data === undefined ? 'carregando' : 'pronta';
}

/**
 * A frase que a tela escreve quando a inspeção é concluída.
 *
 * É ela que faz "o problema não desaparecer ao finalizar o formulário": sem
 * essa linha, quem preencheu sai da tela achando que terminou, e o efeito
 * prático é o mesmo de encerrar os achados.
 */
export function oQueFicouAberto(resultado: {
  readonly ocorrenciasAbertas: number;
  readonly criticasAbertas: number;
}): string {
  const { ocorrenciasAbertas: abertas, criticasAbertas: criticas } = resultado;
  if (abertas === 0) return 'Inspeção concluída, sem nenhuma ocorrência em aberto.';
  const quantas = abertas === 1 ? '1 ocorrência continua em aberto' : `${String(abertas)} ocorrências continuam em aberto`;
  if (criticas === 0) return `Inspeção concluída. ${quantas} e aparece na fila de ocorrências.`;
  const quantasCriticas = criticas === 1 ? '1 é crítica' : `${String(criticas)} são críticas`;
  return `Inspeção concluída. ${quantas}, e ${quantasCriticas} — ela não se encerra sem O.S. ou sem o que foi feito escrito.`;
}

/**
 * Quantos dias sem inspeção, escrito — e o nulo dito por extenso.
 *
 * Máquina nunca inspecionada não vira "—": o risco que o v6 escreve para a
 * ferramenta 1 é "máquina utilizada sem verificação registrada", e um
 * travessão se lê como ausência de dado, não como ausência de inspeção.
 */
export function coberturaEscrita(linha: LinhaDeCobertura): string {
  if (linha.ultimaEm === null) return 'Nunca inspecionada';
  const dias = linha.diasSemInspecao ?? 0;
  if (dias === 0) return 'Inspecionada hoje';
  if (dias === 1) return 'Há 1 dia';
  return `Há ${String(dias)} dias`;
}

/**
 * O que dá para fazer agora, e o motivo quando não dá.
 *
 * SEM A OCORRÊNCIA como parâmetro, diferente do que os módulos 09 e 14 fazem,
 * e a diferença é real: lá o estado do pedido e da medição decide o que cabe
 * (não se aprova duas vezes, não se emite antes de aprovar). Aqui a fila só
 * traz ocorrência em aberto — a view a filtra —, então a única coisa que
 * impede a ação é a licença. Receber a ocorrência e ignorá-la daria a
 * impressão de que o estado dela pesa na decisão, e ele não pesa.
 */
export function porqueNaoDa(
  acao: 'encerrar' | 'bloqueio',
  permissoes: readonly string[],
): string | undefined {
  const licenca: Record<typeof acao, string> = {
    encerrar: 'ocorrencia.encerrar',
    bloqueio: 'inspecao.gravar',
  };
  if (!permissoes.includes(licenca[acao])) {
    return `Você não tem a licença ${licenca[acao]}. Peça a liberação em Cadastros · Governança.`;
  }
  return undefined;
}

/**
 * Se este encerramento é aceito, e o que falta quando não é.
 *
 * A regra do banco, repetida aqui só para a tela desligar o botão com a razão
 * escrita em vez de deixar a pessoa descobrir no erro: ocorrência crítica sem
 * O.S. exige pelo menos 20 caracteres de justificativa.
 */
export function oQueFaltaParaEncerrar(
  ocorrencia: Pick<Ocorrencia, 'criticidade'>,
  encerramento: { readonly desfecho: Desfecho | null; readonly ordemId: string; readonly justificativa: string },
): string | undefined {
  if (encerramento.desfecho === null) return 'Escolha como a ocorrência termina.';
  if (encerramento.desfecho === 'ordem_de_servico') {
    return encerramento.ordemId.trim() === '' ? 'Escolha a O.S. que vai tratar a ocorrência.' : undefined;
  }
  if (ocorrencia.criticidade === 'critico' && encerramento.justificativa.trim().length < 20) {
    return 'Ocorrência crítica sem O.S. exige pelo menos 20 caracteres dizendo o que foi feito.';
  }
  return undefined;
}

/** As inspeções que ainda não foram concluídas, para a tela abrir nelas. */
export function emAndamento(inspecoes: readonly Inspecao[]): readonly Inspecao[] {
  return inspecoes.filter((i) => i.situacao === 'em_andamento');
}

/* ------------------------------------------------ responder na mesa (20/10) */

/**
 * O RASCUNHO DE UM ITEM enquanto a pessoa responde na mesa. Achado dos robôs
 * no QAS (20/10/2026): a mesa abria a inspeção e não tinha onde responder, e o
 * "Concluir" recusava para sempre. O celular responde no checklist do campo;
 * a mesa responde item a item, pela mesma rota de respostas, com as mesmas
 * três saídas do celular — conforme, não conforme, não se aplica.
 */
export type EscolhaDoItem = 'conforme' | 'nao_conforme' | 'nao_se_aplica';

export interface RascunhoDoItem {
  readonly escolha: EscolhaDoItem | null;
  /** A medida como a pessoa digitou ("12,5"). */
  readonly medida: string;
  /** O que viu no não conforme, ou a resposta do item de texto. */
  readonly texto: string;
}

/** O rascunho de partida: o que já está gravado, ou nada. */
export function rascunhoDe(item: ItemDaInspecao): RascunhoDoItem {
  const r = item.resposta;
  if (r === null) return { escolha: null, medida: '', texto: '' };
  const escolha: EscolhaDoItem | null = r.naoSeAplica
    ? 'nao_se_aplica'
    : r.conforme === true
      ? 'conforme'
      : r.conforme === false
        ? 'nao_conforme'
        : null;
  return {
    escolha,
    medida: r.medida === null ? '' : formatarAteDuasCasas(r.medida),
    texto: r.texto,
  };
}

export interface CorpoDaResposta {
  readonly itemId: string;
  readonly conforme?: boolean | null;
  readonly naoSeAplica?: boolean;
  readonly medida?: number | null;
  readonly texto?: string;
}

/**
 * O corpo que vai ao servidor, ou o que falta — escrito para quem responde.
 * O não conforme pede o que foi visto (no celular, a foto e a observação; na
 * mesa, a observação, e a foto se anexa depois na ocorrência).
 */
export function corpoDaResposta(
  item: Pick<ItemDoChecklist, 'id' | 'tipo' | 'unidade'>,
  rascunho: RascunhoDoItem,
): { readonly corpo: CorpoDaResposta } | { readonly falta: string } {
  if (rascunho.escolha === 'nao_se_aplica') return { corpo: { itemId: item.id, naoSeAplica: true } };
  if (item.tipo === 'medida') {
    const medida = interpretarNumero(rascunho.medida);
    if (medida === null) return { falta: `Informe a medida${item.unidade ? ` em ${item.unidade}` : ''}, ou marque que não se aplica.` };
    return { corpo: { itemId: item.id, medida, naoSeAplica: false } };
  }
  if (item.tipo === 'texto') {
    const texto = rascunho.texto.trim();
    if (texto === '') return { falta: 'Escreva a resposta, ou marque que não se aplica.' };
    return { corpo: { itemId: item.id, texto, naoSeAplica: false } };
  }
  if (rascunho.escolha === null) return { falta: 'Marque conforme, não conforme ou não se aplica.' };
  if (rascunho.escolha === 'conforme') return { corpo: { itemId: item.id, conforme: true, naoSeAplica: false } };
  const texto = rascunho.texto.trim();
  if (texto.length < 3) return { falta: 'Escreva o que viu: é o que a ocorrência vai mostrar a quem trata.' };
  return { corpo: { itemId: item.id, conforme: false, naoSeAplica: false, texto } };
}

/** A resposta gravada, escrita para a lista ("Conforme", "12,5 bar"). */
export function respostaEscrita(item: ItemDaInspecao): string | null {
  const r = item.resposta;
  if (r === null) return null;
  if (r.naoSeAplica) return 'Não se aplica';
  if (item.tipo === 'medida') return r.medida === null ? null : `${formatarAteDuasCasas(r.medida)}${item.unidade ? ` ${item.unidade}` : ''}`;
  if (item.tipo === 'texto') return r.texto;
  if (r.conforme === true) return 'Conforme';
  if (r.conforme === false) return r.texto ? `Não conforme · ${r.texto}` : 'Não conforme';
  return null;
}

/**
 * Por que ainda não dá para concluir — a mesma regra do servidor, dita antes
 * do clique, com o número de itens em branco.
 */
export function porqueNaoConclui(itens: readonly ItemDaInspecao[]): string | null {
  const faltam = itens.filter((i) => i.resposta === null).length;
  if (itens.length === 0) return 'O checklist desta inspeção não tem itens.';
  if (faltam === 0) return null;
  return faltam === 1
    ? 'Falta responder 1 item. Marque "não se aplica" no que não couber nesta máquina.'
    : `Faltam responder ${formatarInteiro(faltam)} itens. Marque "não se aplica" no que não couber nesta máquina.`;
}

/* ------------------------------------------- montar o checklist (achado 15) */

/**
 * A PRÓXIMA ORDEM do checklist: a maior que já existe, mais um. A janela de
 * acrescentar abria com a ordem vazia e o botão desligado sem dizer por quê.
 */
export function proximaOrdem(itens: readonly Pick<ItemDoChecklist, 'ordem'>[], depoisDe?: number | null): number {
  const maior = itens.reduce((m, i) => Math.max(m, i.ordem), 0);
  return Math.max(maior, depoisDe ?? 0) + 1;
}

/** Por que o item ainda não pode ser acrescentado — o motivo escrito do botão desligado. */
export function porqueNaoAcrescenta(item: {
  readonly ordem: string;
  readonly descricao: string;
  readonly tipo: string;
  readonly unidade: string;
}): string | null {
  const ordem = interpretarNumero(item.ordem);
  if (ordem === null || !Number.isInteger(ordem) || ordem < 1) return 'Informe a ordem: a posição do item na lista, de 1 em diante.';
  if (item.descricao.trim() === '') return 'Escreva o que se verifica neste item.';
  if (item.tipo === 'medida' && item.unidade.trim() === '') return 'Informe a unidade da medida.';
  return null;
}

/**
 * OS CHECKLISTS QUE COBREM A MÁQUINA (31/10/2026, o autopreenchimento levado à
 * inspeção): a cobertura já diz que checklist publicado vale para cada
 * máquina, pelo modelo ou pela configuração dela. Ao abrir a inspeção, esses
 * vêm primeiro; sendo um só, ele já vem escolhido. Os outros publicados
 * continuam a um clique.
 */
export function checklistsDaMaquina(
  cobertura: readonly Pick<LinhaDeCobertura, 'equipamentoId' | 'checklistId'>[],
  equipamentoId: string,
): readonly string[] {
  if (equipamentoId === '') return [];
  return [...new Set(cobertura.filter((l) => l.equipamentoId === equipamentoId).map((l) => l.checklistId))];
}
